import {
  Component,
  forwardRef,
  ViewEncapsulation,
  ChangeDetectorRef,
  OnDestroy,
  ChangeDetectionStrategy,
  computed,
  effect,
  input,
  output,
  signal,
  viewChild,
  inject,
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule} from '@angular/forms';
import {DateAdapter} from '@angular/material/core';
import {MatCalendar, MatDatepickerModule} from '@angular/material/datepicker';
import {LuxonDateAdapter, provideLuxonDateAdapter} from '@angular/material-luxon-adapter';
import {CdkConnectedOverlay, CdkOverlayOrigin} from '@angular/cdk/overlay';
import {uniqId, coerceBoolean} from '@matchsource/utils';
import {DateTime, DurationLike} from 'luxon';
import {ReplaySubject, noop} from 'rxjs';
import {DatetimeTimePipe} from './datetime-time.pipe';
import {NgxMaskDirective} from 'ngx-mask';
import {AsyncPipe} from '@angular/common';
import {parseDateTime} from '@matchsource/date';

type Meridiem = 'AM' | 'PM';

type OnChangeFn = (value: string | null) => void;
type OnTouchedFn = () => void;

export enum Mode {
  Daytime = 'daytime',
  Day = 'day',
  Month = 'month',
}

const MODE_CONFIG = {
  [Mode.Daytime]: {
    FORMAT: 'MM/dd/yyyy hh:mm a',
    PLACEHOLDER: 'mm/dd/yyyy ##:## PM',
    // MASK: '00/00/0000 00:00 PM',
    MASK: null as string,
  },
  [Mode.Day]: {
    FORMAT: 'MM/dd/yyyy',
    PLACEHOLDER: 'mm/dd/yyyy',
    MASK: '00/00/0000',
  },
  [Mode.Month]: {
    FORMAT: 'MM/yyyy',
    PLACEHOLDER: 'mm/yyyy',
    MASK: '00/0000',
  },
};

// Only the calendar's own labels are driven by these. The text input keeps parsing and
// formatting itself through luxon, so the datepicker adapter never touches the control value.
const MS_DATE_FORMATS = {
  parse: {
    dateInput: 'MM/dd/yyyy',
  },
  display: {
    dateInput: 'MM/dd/yyyy',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'DDD',
    monthYearA11yLabel: 'MMMM yyyy',
  },
};

/**
 * Material's month view asks the adapter for 'narrow' weekday names, which luxon renders
 * as single letters. The calendar this replaced used three-letter labels, so widen them.
 */
class MsLuxonDateAdapter extends LuxonDateAdapter {
  override getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    return super.getDayOfWeekNames(style === 'narrow' ? 'short' : style);
  }
}

@Component({
  selector: 'ms-datetime-picker',
  templateUrl: './datetime-picker.component.html',
  styleUrls: ['./datetime-picker.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatetimePickerComponent),
      multi: true,
    },
    provideLuxonDateAdapter(MS_DATE_FORMATS),
    {provide: DateAdapter, useClass: MsLuxonDateAdapter},
  ],
  imports: [
    FormsModule,
    NgxMaskDirective,
    MatDatepickerModule,
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    AsyncPipe,
    DatetimeTimePipe,
  ],
})
export class DatetimePickerComponent implements ControlValueAccessor, OnDestroy {
  readonly mode = input(Mode.Daytime, {
    transform: (value: `${Mode}`): Mode => value as Mode,
  });
  readonly name = input<string>();
  readonly max = input<DateTime | Date | MsApp.DateString>();
  readonly min = input<DateTime | Date | MsApp.DateString>();
  readonly customClass = input<string>();
  readonly hideNote = input(false);
  readonly disabled = input(false, {transform: coerceBoolean});

  readonly changeValue = output<unknown>();

  readonly calendar = viewChild<MatCalendar<DateTime>>(MatCalendar);

  private readonly cdRef = inject(ChangeDetectorRef);

  private readonly controlDisabled = signal(false);

  private readonly initialDate = DateTime.now();
  private internalValue = '';

  private onValueChange: OnChangeFn = noop;
  private onTouch: OnTouchedFn = noop;

  readonly id = uniqId();
  readonly value$ = new ReplaySubject<string>(1);

  readonly isDisabled = computed(() => this.disabled() || this.controlDisabled());
  readonly format = computed(() => MODE_CONFIG[this.mode()].FORMAT);
  readonly placeholder = computed(() => MODE_CONFIG[this.mode()].PLACEHOLDER);
  readonly mask = computed(() => MODE_CONFIG[this.mode()].MASK);

  readonly isMonthMode = computed(() => this.mode() === Mode.Month);

  /** Daytime keeps the time strip below the calendar and stays open on date selection. */
  readonly hasTime = computed(() => this.mode() === Mode.Daytime);

  /** Month mode picks from the 12-month grid, which is Material's 'year' view. */
  readonly startView = computed<'month' | 'year'>(() => (this.isMonthMode() ? 'year' : 'month'));

  // The instance id keeps a selector scopable to one picker, as the previous popup's
  // `.dp-popup.{id}` class did. Pages with several pickers rely on it.
  readonly panelClass = computed(() => ['ms-datepicker-panel', `ms-datepicker-${this.mode()}`, this.id]);

  readonly isOpen = signal(false);
  readonly activeDate = signal<DateTime>(DateTime.now());
  readonly selectedDate = signal<DateTime | null>(null);

  readonly minDate = computed(() => this.boundary(this.min()));
  readonly maxDate = computed(() => this.boundary(this.max()));

  patterns = {
    0: {pattern: /\d/},
    P: {pattern: /[APap]/},
    M: {pattern: /[Mm]/},
  };

  isYearActive = false;
  isHoursActive = false;
  isMinutesActive = false;

  hours: number | null = null;
  minutes: number | null = null;
  meridiem: Meridiem = 'PM';

  displayDate: DateTime = this.initialDate.set({day: 0});
  displayYear = +DateTime.now().toFormat('yyyy');
  displayMonth = DateTime.now().toFormat('MMM');

  constructor() {
    // We render our own calendar header, so Material's state changes have to be mirrored
    // back onto displayDate — keyboard navigation can move across a month or year boundary.
    effect(onCleanup => {
      const calendar = this.calendar();

      if (!calendar) {
        return;
      }

      // Sync once on attach too: Material clamps its initial activeDate into
      // [minDate, maxDate], and that clamp does not emit stateChanges.
      this.syncHeaderFromCalendar(calendar);

      const subscription = calendar.stateChanges.subscribe(() => this.syncHeaderFromCalendar(calendar));

      onCleanup(() => subscription.unsubscribe());
    });
  }

  get value(): string {
    return this.internalValue;
  }

  set value(value: string) {
    this.internalValue = value;
    setTimeout(() => this.value$.next(value));
  }

  registerOnChange(fn: OnChangeFn): void {
    this.onValueChange = fn;
  }

  registerOnTouched(fn: OnTouchedFn): void {
    this.onTouch = fn;
  }

  writeValue(value: string | DateTime | null): void {
    let date: DateTime | null = null;

    if (value) {
      date = this.hasTime() ? parseDateTime(value) : DateTime.fromFormat(value as string, this.format(), {zone: 'utc'});
    }

    this.applyDate(date);
  }

  setDisabledState(disabled: boolean): void {
    this.controlDisabled.set(disabled);
  }

  setValue(value: string | null): void {
    const format = this.format();
    const isValid = !!value && DateTime.fromFormat(value, format).isValid;
    const date = isValid ? DateTime.fromFormat(value, format) : null;

    this.applyDate(date);
    this.onValueChange(isValid ? value : null);
  }

  blur(): void {
    this.onTouch();
  }

  onInputChange(event: Event): void {
    const value = this.normalizeInputValue(event);
    const format = this.format();
    const isValid = DateTime.fromFormat(value, format).toFormat(format) === value;

    if (isValid || value === '') {
      this.setValue(value);
    }
  }

  setDateTime(event: Event): void {
    const value = this.normalizeInputValue(event);
    const format = this.format();
    const isValid = DateTime.fromFormat(value, format).toFormat(format) === value;

    this.setValue(isValid ? value : null);
  }

  setDisplayYear(): void {
    this.displayDate = this.displayDate.set({year: this.displayYear});

    this.moveCalendarTo(this.displayDate);
  }

  /** Day selection from the calendar. Daytime stays open so the time strip remains reachable. */
  onCalendarSelect(date: DateTime | null): void {
    if (!date) {
      return;
    }

    this.applySelectedDate(date);

    if (!this.hasTime()) {
      this.close();
    }
  }

  /** Month selection from the 12-month grid. */
  onMonthSelected(date: DateTime): void {
    if (!this.isMonthMode()) {
      return;
    }

    this.applySelectedDate(date);
    this.close();
  }

  setHours(event: Event): void {
    const hours = +(event.target as HTMLInputElement).value || 0;

    if (hours > 12) {
      return;
    }

    const format = this.format();
    let date = this.value ? DateTime.fromFormat(this.value, format) : DateTime.now();

    date = date.set({hour: hours});

    this.setValue(date.toFormat(format));
  }

  setMinutes(event: Event): void {
    const minutes = +(event.target as HTMLInputElement).value || 0;

    if (minutes > 59) {
      return;
    }

    const format = this.format();
    let date = this.value ? DateTime.fromFormat(this.value, format) : DateTime.now();

    date = date.set({minute: minutes});

    this.setValue(date.toFormat(format));
  }

  toggleMeridiem(): void {
    const format = this.format();
    let date = this.value ? DateTime.fromFormat(this.value, format) : DateTime.now();

    date = this.meridiem === 'AM' ? date.plus({hours: 12}) : date.minus({hours: 12});

    this.setValue(date.toFormat(format));
  }

  onChange(value: any): void {
    this.changeValue.emit(value);
  }

  onLeftNav(): void {
    const delta: DurationLike = this.isMonthMode() ? {years: 1} : {months: 1};

    this.displayDate = this.displayDate.minus(delta);
    this.updateDisplayedMonthAndYear();
    this.moveCalendarTo(this.displayDate);
  }

  onRightNav(): void {
    const delta: DurationLike = this.isMonthMode() ? {years: 1} : {months: 1};

    this.displayDate = this.displayDate.plus(delta);
    this.updateDisplayedMonthAndYear();
    this.moveCalendarTo(this.displayDate);
  }

  onClose(): void {
    const format = this.format();
    const parsedDate = this.value ? DateTime.fromFormat(this.value, format) : null;

    this.displayDate = parsedDate?.isValid ? parsedDate : DateTime.now();
    this.updateDisplayedMonthAndYear();
    this.moveCalendarTo(this.displayDate);
  }

  onOpen(): void {
    this.displayDate = this.displayDate || DateTime.now();
    this.updateDisplayedMonthAndYear();
    this.moveCalendarTo(this.displayDate);
  }

  openDatePicker(): void {
    if (this.isDisabled()) {
      return;
    }

    this.isOpen.set(true);
    this.onOpen();
  }

  close(): void {
    if (!this.isOpen()) {
      return;
    }

    this.isOpen.set(false);
    this.isYearActive = false;
    this.isHoursActive = false;
    this.isMinutesActive = false;
    this.blur();
    this.onClose();
  }

  onOverlayKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }

  activateYear(): void {
    this.isYearActive = true;
  }

  deactivateYear(): void {
    this.isYearActive = false;
  }

  activateHours(): void {
    this.isHoursActive = true;
  }

  onHoursBlur(event: Event): void {
    this.setHours(event);
    this.isHoursActive = false;
  }

  activateMinutes(): void {
    this.isMinutesActive = true;
  }

  onMinutesBlur(event: Event): void {
    this.setMinutes(event);
    this.isMinutesActive = false;
  }

  ngOnDestroy(): void {
    this.value$.complete();
  }

  private applySelectedDate(selected: DateTime): void {
    const format = this.format();
    let date = this.value ? DateTime.fromFormat(this.value, format) : DateTime.now();

    date = date.set({
      year: selected.year,
      month: selected.month,
      day: selected.day,
    });

    this.blur();
    this.setValue(date.toFormat(format));
  }

  private boundary(value: DateTime | Date | MsApp.DateString | undefined): DateTime | null {
    if (!value) {
      return null;
    }

    const date = parseDateTime(value);

    return this.isMonthMode() ? date.startOf('month') : date.startOf('day');
  }

  private moveCalendarTo(date: DateTime): void {
    this.activeDate.set(date);

    const calendar = this.calendar();

    if (!calendar) {
      return;
    }

    calendar.activeDate = date;

    // Material clamps activeDate into [minDate, maxDate], so the month it ends up
    // showing is not always the one we asked for. The header has to follow the
    // calendar, not our request, or the two disagree.
    this.syncHeaderFromCalendar(calendar);
  }

  private syncHeaderFromCalendar(calendar: MatCalendar<DateTime>): void {
    this.displayDate = calendar.activeDate;
    this.updateDisplayedMonthAndYear();
    this.cdRef.markForCheck();
  }

  private applyDate(date: DateTime | null): void {
    const format = this.format();

    if (date) {
      this.value = date.toFormat(format);
      this.hours = +date.toFormat('hh');
      this.minutes = +date.toFormat('mm');
      this.meridiem = date.toFormat('a') as Meridiem;
      this.displayDate = date;
    } else {
      this.value = '';
      this.displayDate = DateTime.now();
      this.hours = null;
      this.minutes = null;
      this.meridiem = 'PM';
    }

    this.displayYear = +this.displayDate.toFormat('yyyy');
    this.displayMonth = this.displayDate.toFormat('MMM');
    this.selectedDate.set(date);

    this.moveCalendarTo(this.displayDate);
    this.cdRef.markForCheck();
  }

  private normalizeInputValue(event: Event): string {
    return (event.target as HTMLInputElement).value.split(/\s+/).join(' ').toUpperCase();
  }

  private updateDisplayedMonthAndYear(): void {
    this.displayYear = +this.displayDate.toFormat('yyyy');
    this.displayMonth = this.displayDate.toFormat('MMM');
  }
}
